import { useContext, useEffect, useRef, useState } from "react";
import { StateContext } from "../../../components/context/StateProvider";
import { cargarProductosGrupo } from "../helpers/ticketGrupoN2";

/**
 * Núcleo compartido de la precarga de productos de Nave 2 (grupos y pedidos sueltos).
 *
 * Cada elemento se trata como un grupo (`{ id, pedidos }`). La caché es por
 * ejercicio + elemento + pedidos: el listado se refresca con objetos nuevos, pero un
 * elemento solo se vuelve a cargar si cambia su clave.
 *
 * @param {Array} grupos - Elementos con `id` y `pedidos` [{numero, tipo}]
 * @param {string} [etiqueta] - Nombre del elemento para el log de errores
 * @returns {Object<string|number, Array|null|undefined>} Productos por id: `undefined`
 *   mientras carga, `null` si la carga falló
 */
const useProductosPrecargadosN2 = (grupos, etiqueta = 'grupo') => {
    const { state } = useContext(StateContext);
    const year = state.year;

    // clave → productos | null (fallo). Las claves en curso viven en `enCurso`
    const [productos, setProductos] = useState({});
    const enCurso = useRef(new Set());
    const montado = useRef(true);

    useEffect(() => {
        montado.current = true;
        return () => {
            montado.current = false;
        };
    }, []);

    const claveDe = (grupo) =>
        `${year}|${grupo.id}|${(grupo.pedidos || []).map((pedido) => pedido.numero || pedido).join(',')}`;

    useEffect(() => {
        grupos.forEach((grupo) => {
            const clave = claveDe(grupo);
            if (clave in productos || enCurso.current.has(clave)) return;

            enCurso.current.add(clave);
            cargarProductosGrupo(grupo, year)
                .catch((error) => {
                    console.error(`Error al precargar los productos del ${etiqueta} ${grupo.id}:`, error);
                    return null;
                })
                .then((resultado) => {
                    enCurso.current.delete(clave);
                    if (montado.current) {
                        setProductos((previo) => ({ ...previo, [clave]: resultado }));
                    }
                });
        });
        // `productos` no va en deps: lo que ya está cargado se comprueba al llegar elementos
        // nuevos, y volver a recorrer al guardar cada resultado no aportaría nada
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [grupos, year]);

    return Object.fromEntries(grupos.map((grupo) => [grupo.id, productos[claveDe(grupo)]]));
};

export default useProductosPrecargadosN2;
